/**
 * LocationAlertBanner — Collaborative Performance Nudge
 * =====================================================
 * Soft, supportive design. Not alarming — focuses on partnership
 * and working together to improve metrics below target.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { TrendingDown, X, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { authFetch } from '@/utils/authFetch';
import { API_URL } from '@/config/apiConfig';

export function LocationAlertBanner() {
  const { user} = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchAlerts = useCallback(async (signal) => {
    if (!user) return;
    try {
      const res = await authFetch(`${API_URL}/api/location-alerts/active`, { signal });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
        if (data.alerts?.length > 0) setDismissed(false);
      }
    } catch {}
  }, [user]);

  useEffect(() => {
    const controller = new AbortController();
    fetchAlerts(controller.signal);
    const interval = setInterval(() => fetchAlerts(controller.signal), 60000);
    return () => { controller.abort(); clearInterval(interval); };
  }, [fetchAlerts]);

  const handleAcknowledge = async (locationId, metric) => {
    try {
      await authFetch(`${API_URL}/api/location-alerts/acknowledge/${locationId}/${metric}`, {
        method: 'POST' });
      fetchAlerts();
    } catch {}
  };

  if (dismissed || alerts.length === 0) return null;

  return (
    <div
      className="w-full border-b bg-amber-50/80 border-amber-200/60"
      data-testid="location-alert-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-amber-100 flex items-center justify-center">
              <TrendingDown className="w-3.5 h-3.5 text-amber-600" />
            </div>
            <span className="text-sm font-medium text-amber-800">
              {alerts.length} location{alerts.length > 1 ? 's' : ''} below target — let's work on this
            </span>
            {alerts.length > 1 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 transition-colors"
                data-testid="alert-expand-toggle"
              >
                {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {expanded ? 'Less' : 'Details'}
              </button>
            )}
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="p-1 hover:bg-amber-200/50 rounded text-amber-500 hover:text-amber-700 transition-colors"
            data-testid="alert-dismiss-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* First alert inline */}
        {alerts[0] && !expanded && (
          <div className="flex items-center gap-3 mt-1.5 text-xs text-amber-700" data-testid="alert-preview">
            <span className="font-medium text-amber-800">{alerts[0].location_name}</span>
            <span>Current: ${alerts[0].current_value?.toFixed(2)}</span>
            <span className="text-amber-500">Target: ${alerts[0].threshold?.toFixed(2)}</span>
            <span className="font-medium">({alerts[0].gap_pct}% gap)</span>
            <button
              onClick={() => handleAcknowledge(alerts[0].location_id, alerts[0].metric)}
              className="ml-auto flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 font-medium transition-colors"
              data-testid={`ack-alert-${alerts[0].location_id}`}
            >
              Got it <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Expanded view */}
        {expanded && (
          <div className="mt-2 space-y-1" data-testid="alerts-expanded">
            {alerts.map((a, i) => (
              <div
                key={i}
                className="flex items-center gap-3 text-xs px-3 py-2 rounded-lg bg-white/60 border border-amber-100"
                data-testid={`alert-row-${a.location_id}`}
              >
                <span className="font-medium text-slate-700 w-32 truncate">{a.location_name}</span>
                <span className="text-slate-500 w-14">{a.metric?.toUpperCase()}</span>
                <span className="text-slate-700 w-20">${a.current_value?.toFixed(2)}</span>
                <span className="text-slate-400 w-24">Target: ${a.threshold?.toFixed(2)}</span>
                <span className="font-medium text-amber-700 w-20">{a.gap_pct}% gap</span>
                <button
                  onClick={() => handleAcknowledge(a.location_id, a.metric)}
                  className="ml-auto text-xs text-amber-600 hover:text-amber-800 font-medium"
                >
                  Acknowledge
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
